import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import LightBackground from '@/components/LightBackground';
import { 
  Users, 
  Settings,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Plus,
  Eye,
  DollarSign,
  Database,
  MessageCircle,
  BookOpen
} from 'lucide-react';
import CreateClientDialog from '@/components/CreateClientDialog';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import {
  fetchClients,
  createClient,
  Client,
  getActiveClients,
  getExpiringClients,
  getPendingClients,
  getPendingLifetimeClients,
  getPendingMonthlyClients,
  getExpiredMonthlyClients,
  getActiveLifetimeClients,
  getActiveMonthlyClients
} from '@/services/clientService';
import { mockClients } from '@/data/clientsData';
import { supabase } from '@/integrations/supabase/client';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, role, loading: authLoading, signOut } = useAuth();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [useMockData, setUseMockData] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [revenuePeriod, setRevenuePeriod] = useState<'month' | '3months' | '6months' | 'year'>('month');

  // Check auth and redirect if not admin
  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/login');
      } else if (role !== 'admin') {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
      }
    }
  }, [user, role, authLoading, navigate]);

  // Fetch clients
  const loadClients = async () => {
    setLoading(true);
    try {
      const data = await fetchClients();
      if (data.length === 0) {
        // Use mock data if no real clients exist
        setUseMockData(true);
        // Convert mock data to match Client interface
        const convertedMockData: Client[] = mockClients.map((m) => ({
          id: m.id.toString(),
          user_id: null,
          name: m.name,
          email: m.email,
          plan: m.plan,
          plan_price: m.planPrice,
          status: m.status,
          paid: m.status !== 'expired',
          subscription_start: m.subscriptionStart,
          subscription_end: m.subscriptionEnd,
          last_active_at: m.lastActiveDate.toISOString(),
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          academy_progress: m.academyProgress.map((ap) => ({
            id: `${m.id}-${ap.academyId}`,
            client_id: m.id.toString(),
            academy_id: ap.academyId,
            academy_name: ap.academyName,
            completed_modules: ap.completedModules,
            total_modules: ap.totalModules,
          })),
        }));
        setClients(convertedMockData);
      } else {
        setUseMockData(false);
        setClients(data);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients. Using demo data.');
      setUseMockData(true);
      // Use mock data on error
      const convertedMockData: Client[] = mockClients.map((m) => ({
        id: m.id.toString(),
        user_id: null,
        name: m.name,
        email: m.email,
        plan: m.plan,
        plan_price: m.planPrice,
        status: m.status,
        paid: m.status !== 'expired',
        subscription_start: m.subscriptionStart,
        subscription_end: m.subscriptionEnd,
        last_active_at: m.lastActiveDate.toISOString(),
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        academy_progress: m.academyProgress.map((ap) => ({
          id: `${m.id}-${ap.academyId}`,
          client_id: m.id.toString(),
          academy_id: ap.academyId,
          academy_name: ap.academyName,
          completed_modules: ap.completedModules,
          total_modules: ap.totalModules,
        })),
      }));
      setClients(convertedMockData);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (user && role === 'admin') {
      loadClients();
    }
  }, [user, role]);

  const handleSignOut = async () => {
    await signOut();
  };

  const handleCreateClient = async (clientData: Parameters<typeof createClient>[0]) => {
    try {
      await createClient(clientData);
      toast.success('Client created successfully!');
      await loadClients();
    } catch (error) {
      toast.error('Failed to create client. Please try again.');
      throw error;
    }
  };

  // Check if a client is a test account
  const isTestAccount = (client: Client) => {
    const testEmails = ['test@test.com', 'teste.teste@gmail.com'];
    const isTestEmail = testEmails.includes(client.email.toLowerCase());
    const isTestPlan = client.plan.toLowerCase().includes('test');
    return isTestEmail || isTestPlan;
  };

  // Filter out test accounts for real metrics
  const realClients = clients.filter(c => !isTestAccount(c));

  const pendingClients = getPendingClients(realClients);
  const pendingLifetimeClients = getPendingLifetimeClients(realClients);
  const pendingMonthlyClients = getPendingMonthlyClients(realClients);
  const expiredMonthlyClients = getExpiredMonthlyClients(realClients);
  const activeLifetimeClients = getActiveLifetimeClients(realClients);
  const activeMonthlyClients = getActiveMonthlyClients(realClients);

  // Dynamic calculations
  const totalClients = clients.length;
  const activeClients = getActiveClients(realClients);
  const expiringClients = getExpiringClients(realClients);

  // Get new clients this month (only real paying clients)
  const getNewClientsThisMonth = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return realClients.filter(c => 
      new Date(c.created_at) >= startOfMonth && 
      c.paid && 
      c.amount_paid && 
      c.amount_paid > 0
    ).length;
  };

  // Get test clients
  const testClients = clients.filter(c => isTestAccount(c));

  // Calculate revenue based on period - only real paying clients with actual amount_paid
  const getRevenueByPeriod = (period: 'month' | '3months' | '6months' | 'year') => {
    const now = new Date();
    let startDate: Date;
    
    switch (period) {
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case '3months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        break;
      case '6months':
        startDate = new Date(now.getFullYear(), now.getMonth() - 5, 1);
        break;
      case 'year':
        startDate = new Date(now.getFullYear(), 0, 1);
        break;
    }

    // Filter to only real paying clients (not test accounts, with actual payment)
    const realPaidClients = realClients.filter(c => 
      c.paid && 
      c.amount_paid && 
      c.amount_paid > 0 && 
      new Date(c.subscription_start) >= startDate
    );

    const lifetimePayments = realPaidClients.filter(c => 
      c.plan.toLowerCase().includes('lifetime')
    );

    const monthlyPayments = realPaidClients.filter(c => 
      c.plan.toLowerCase().includes('monthly')
    );

    return {
      lifetimeRevenue: lifetimePayments.reduce((sum, c) => sum + (c.amount_paid || 0), 0),
      monthlyRevenue: monthlyPayments.reduce((sum, c) => sum + (c.amount_paid || 0), 0),
      lifetimeCount: lifetimePayments.length,
      monthlyCount: monthlyPayments.length,
      total: realPaidClients.reduce((sum, c) => sum + (c.amount_paid || 0), 0)
    };
  };

  const revenueData = getRevenueByPeriod(revenuePeriod);
  const periodLabels: Record<typeof revenuePeriod, string> = {
    'month': 'This Month',
    '3months': 'Last 3 Months',
    '6months': 'Last 6 Months',
    'year': 'This Year'
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <LightBackground variant="subtle" />

      {/* Header - Mobile Optimized */}
      <motion.header
        className="relative z-20 p-3 sm:p-6 border-b border-border bg-white/80 backdrop-blur-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Top row */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-4">
            <Link to="/" className="flex items-center">
              <span className="text-lg sm:text-xl font-semibold text-foreground">
                Faceless Aura <span className="text-primary">Club</span>
              </span>
            </Link>
            <div className="h-4 sm:h-6 w-px bg-border" />
            <span className="text-xs sm:text-sm font-medium text-primary">Admin</span>
            {useMockData && (
              <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                Demo
              </span>
            )}
          </div>
          <div className="flex items-center gap-1 sm:gap-4">
            {expiringClients.length > 0 && (
              <div className="hidden sm:flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 border border-amber-200">
                <AlertTriangle className="w-4 h-4 text-amber-600" />
                <span className="text-xs text-amber-700">{expiringClients.length} expiring</span>
              </div>
            )}
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-muted-foreground hover:text-foreground h-8 w-8 sm:h-10 sm:w-10"
              onClick={loadClients}
            >
              <RefreshCw className="w-4 h-4 sm:w-5 sm:h-5" />
            </Button>
            <Link to="/admin/settings">
              <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground h-8 w-8 sm:h-10 sm:w-10">
                <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
              </Button>
            </Link>
            <div className="hidden sm:block h-6 w-px bg-border" />
            <span className="hidden md:block text-sm text-muted-foreground truncate max-w-[150px]">{user?.email}</span>
            <Button 
              variant="ghost" 
              size="sm" 
              className="text-muted-foreground hover:text-primary text-xs sm:text-sm px-2 sm:px-3" 
              onClick={handleSignOut}
            >
              <span className="hidden sm:inline">Sign Out</span>
              <span className="sm:hidden">Exit</span>
            </Button>
          </div>
        </div>
        
        {/* Mobile alerts row */}
        {expiringClients.length > 0 && (
          <div className="flex sm:hidden items-center gap-2 mt-2 px-2 py-1 rounded-full bg-amber-50 border border-amber-200 w-fit">
            <AlertTriangle className="w-3 h-3 text-amber-600" />
            <span className="text-[10px] text-amber-700">{expiringClients.length} expiring</span>
          </div>
        )}
      </motion.header>

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* Welcome section */}
        <motion.div
          className="mb-4 sm:mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold tracking-tight mb-1 sm:mb-2">
            <span className="text-foreground">Admin Dashboard</span>
          </h1>
          <p className="text-muted-foreground text-xs sm:text-sm">
            <span className="hidden sm:inline">Overview of all members and platform activity • </span>
            Last updated: {new Date().toLocaleTimeString()}
          </p>
        </motion.div>

        {/* Two Large Stats Cards */}
        <motion.div
          className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6 mb-4 sm:mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {/* Financial Card */}
          <div className="p-4 sm:p-6 border border-border bg-white rounded-2xl shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0 mb-4 sm:mb-6">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-foreground">Revenue</h3>
              </div>
              <div className="flex gap-1">
                {(['month', '3months', '6months', 'year'] as const).map((period) => (
                  <button
                    key={period}
                    onClick={() => setRevenuePeriod(period)}
                    className={`px-2 sm:px-3 py-1 sm:py-1.5 text-[10px] sm:text-xs rounded-lg transition-colors ${
                      revenuePeriod === period
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
                    }`}
                  >
                    {period === 'month' ? '1M' : period === '3months' ? '3M' : period === '6months' ? '6M' : '1Y'}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <p className="text-3xl sm:text-4xl font-semibold text-foreground">${revenueData.total.toLocaleString()}</p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">{periodLabels[revenuePeriod]}</p>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-4">
              <div className="p-3 sm:p-4 bg-slate-50 rounded-xl border border-border">
                <p className="text-lg sm:text-2xl font-semibold text-primary">${revenueData.lifetimeRevenue.toLocaleString()}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">Lifetime</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground/60">{revenueData.lifetimeCount} payments</p>
              </div>
              <div className="p-3 sm:p-4 bg-slate-50 rounded-xl border border-border">
                <p className="text-lg sm:text-2xl font-semibold text-primary">${revenueData.monthlyRevenue.toLocaleString()}</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-1">Monthly</p>
                <p className="text-[10px] sm:text-xs text-muted-foreground/60">{revenueData.monthlyCount} payments</p>
              </div>
            </div>
          </div>

          {/* Clients Card */}
          <div className="p-4 sm:p-6 border border-border bg-white rounded-2xl shadow-sm">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
              <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-foreground">Client Overview</h3>
            </div>
            
            <div className="mb-4 sm:mb-6">
              <p className="text-3xl sm:text-4xl font-semibold text-foreground">{totalClients}</p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">Total Clients</p>
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
              <div className="p-2 sm:p-3 bg-green-50 rounded-xl border border-green-100">
                <p className="text-base sm:text-xl font-semibold text-green-600">{getNewClientsThisMonth()}</p>
                <p className="text-[9px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 leading-tight">New</p>
              </div>
              <div className="p-2 sm:p-3 bg-blue-50 rounded-xl border border-blue-100">
                <p className="text-base sm:text-xl font-semibold text-blue-600">{activeMonthlyClients.length}</p>
                <p className="text-[9px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 leading-tight">Monthly</p>
              </div>
              <div className="p-2 sm:p-3 bg-purple-50 rounded-xl border border-purple-100">
                <p className="text-base sm:text-xl font-semibold text-purple-600">{activeLifetimeClients.length}</p>
                <p className="text-[9px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 leading-tight">Lifetime</p>
              </div>
              <div className="p-2 sm:p-3 bg-red-50 rounded-xl border border-red-100">
                <p className="text-base sm:text-xl font-semibold text-red-600">{expiredMonthlyClients.length}</p>
                <p className="text-[9px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 leading-tight">Stopped</p>
              </div>
              <div className="p-2 sm:p-3 bg-amber-50 rounded-xl border border-amber-100">
                <p className="text-base sm:text-xl font-semibold text-amber-600">{clients.filter(c => c.status === 'inactive' && c.paid).length}</p>
                <p className="text-[9px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 leading-tight">Inactive</p>
              </div>
              <div className="p-2 sm:p-3 bg-slate-50 rounded-xl border border-border">
                <p className="text-base sm:text-xl font-semibold text-slate-600">{testClients.length}</p>
                <p className="text-[9px] sm:text-xs text-muted-foreground mt-0.5 sm:mt-1 leading-tight">Test</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2 sm:gap-4 mb-4 sm:mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <Link to="/admin/crm" className="w-full">
            <Button variant="outline" className="w-full h-auto py-3 sm:py-4 flex flex-col gap-1 sm:gap-2 bg-white hover:bg-slate-50 border-border rounded-xl">
              <Database className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              <span className="text-[10px] sm:text-xs">CRM</span>
            </Button>
          </Link>
          <Link to="/admin/content" className="w-full">
            <Button variant="outline" className="w-full h-auto py-3 sm:py-4 flex flex-col gap-1 sm:gap-2 bg-white hover:bg-slate-50 border-border rounded-xl">
              <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              <span className="text-[10px] sm:text-xs">Content</span>
            </Button>
          </Link>
          <Link to="/admin/analytics" className="w-full">
            <Button variant="outline" className="w-full h-auto py-3 sm:py-4 flex flex-col gap-1 sm:gap-2 bg-white hover:bg-slate-50 border-border rounded-xl">
              <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
              <span className="text-[10px] sm:text-xs">Analytics</span>
            </Button>
          </Link>
          <Link to="/admin/affiliates" className="w-full">
            <Button variant="outline" className="w-full h-auto py-3 sm:py-4 flex flex-col gap-1 sm:gap-2 bg-white hover:bg-slate-50 border-border rounded-xl">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              <span className="text-[10px] sm:text-xs">Affiliates</span>
            </Button>
          </Link>
          <Link to="/admin/support" className="w-full">
            <Button variant="outline" className="w-full h-auto py-3 sm:py-4 flex flex-col gap-1 sm:gap-2 bg-white hover:bg-slate-50 border-border rounded-xl">
              <MessageCircle className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              <span className="text-[10px] sm:text-xs">Support</span>
            </Button>
          </Link>
          <Link to="/admin/academies" className="w-full">
            <Button variant="outline" className="w-full h-auto py-3 sm:py-4 flex flex-col gap-1 sm:gap-2 bg-white hover:bg-slate-50 border-border rounded-xl">
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              <span className="text-[10px] sm:text-xs">Academies</span>
            </Button>
          </Link>
          <Button 
            variant="outline" 
            className="w-full h-auto py-3 sm:py-4 flex flex-col gap-1 sm:gap-2 bg-white hover:bg-slate-50 border-border rounded-xl"
            onClick={() => setShowCreateDialog(true)}
          >
            <Plus className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            <span className="text-[10px] sm:text-xs">New Client</span>
          </Button>
        </motion.div>

        {/* Pending Payments Alert */}
        {pendingClients.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="p-4 sm:p-6 border border-amber-200 bg-amber-50 rounded-2xl mb-4 sm:mb-8"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-amber-900 mb-2">
                  {pendingClients.length} Pending Payments
                </h3>
                <p className="text-xs text-amber-700 mb-3">
                  {pendingLifetimeClients.length} lifetime • {pendingMonthlyClients.length} monthly
                </p>
                <div className="flex flex-wrap gap-2">
                  {pendingClients.slice(0, 5).map(client => (
                    <span 
                      key={client.id}
                      className="text-xs px-2 py-1 bg-white rounded-full border border-amber-200 text-amber-800"
                    >
                      {client.name}
                    </span>
                  ))}
                  {pendingClients.length > 5 && (
                    <span className="text-xs px-2 py-1 bg-amber-100 rounded-full text-amber-700">
                      +{pendingClients.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Expiring Soon Alert */}
        {expiringClients.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="p-4 sm:p-6 border border-orange-200 bg-orange-50 rounded-2xl"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-orange-600 shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <h3 className="text-sm font-semibold text-orange-900 mb-2">
                  {expiringClients.length} Expiring Soon
                </h3>
                <p className="text-xs text-orange-700 mb-3">
                  These clients will expire within the next 30 days
                </p>
                <div className="flex flex-wrap gap-2">
                  {expiringClients.slice(0, 5).map(client => (
                    <span 
                      key={client.id}
                      className="text-xs px-2 py-1 bg-white rounded-full border border-orange-200 text-orange-800"
                    >
                      {client.name}
                    </span>
                  ))}
                  {expiringClients.length > 5 && (
                    <span className="text-xs px-2 py-1 bg-orange-100 rounded-full text-orange-700">
                      +{expiringClients.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      <CreateClientDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSubmit={handleCreateClient}
      />
    </div>
  );
};

export default AdminDashboard;
